import { Injectable, signal } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class ConfirmationService {
    private resultSubject = new Subject<boolean>();
    isOpen = signal(false);
    title = signal('Unsaved Changes');
    message = signal('You have unsaved changes. Are you sure you want to leave?');
    confirmButtonText = signal('Leave');
    cancelButtonText = signal('Stay');

    /**
     * Shows a confirmation modal with the given options.
     * If no options are given, it will use the default values.
     * The default values are:
     * title: "Unsaved Changes"
     * message: "You have unsaved changes. Are you sure you want to leave?"
     * confirmText: "Leave"
     * cancelText: "Stay"
     * @param {object} options - The options to use for the confirmation modal.
     * @returns {Observable<boolean>} - An observable that emits the result of the confirmation modal when it is closed.
     */
    confirm(options?: { title?: string; message?: string; confirmText?: string; cancelText?: string }): Observable<boolean> {
        if (options) {
            if (options.title) this.title.set(options.title);
            if (options.message) this.message.set(options.message);
            if (options.confirmText) this.confirmButtonText.set(options.confirmText);
            if (options.cancelText) this.cancelButtonText.set(options.cancelText);
        } else {
            // Reset to defaults
            this.title.set('Unsaved Changes');
            this.message.set('You have unsaved changes. Are you sure you want to leave?');
            this.confirmButtonText.set('Leave');
            this.cancelButtonText.set('Stay');
        }

        this.isOpen.set(true);
        return this.resultSubject.asObservable().pipe(take(1));
    }

    /**
     * Resolve the confirmation modal with the given result.
     * This will emit the result and close the modal.
     * @param {boolean} result - The result of the confirmation modal.
     */
    resolve(result: boolean): void {
        this.isOpen.set(false);
        this.resultSubject.next(result);
    }
}
